/*
 * Which of the sidebar's sections a Member has folded away, remembered in this browser. It is a
 * convenience: storage that is blocked, cleared or holding something else reads as every section at
 * its default.
 */

/** The sidebar's sections that fold. */
export type SidebarSection = "projects"

/** Whether each section is folded. */
export type Folds = Record<SidebarSection, boolean>

/** Each section until the Member folds or opens it. */
export const defaultFolds: Folds = { projects: false }

const storageKey = "beacon.sidebar.sections"

const isSection = (value: unknown): value is SidebarSection =>
  typeof value === "string" && Object.hasOwn(defaultFolds, value)

/**
 * The folds a stored value names, over the defaults, ignoring anything that isn't a section (as
 * `admin`, kept from when the Admin's pages folded in this sidebar). It reads `{ "projects": true }`,
 * and the list of folded sections this browser kept before that.
 */
export function parseFolds(stored: string | null): Folds {
  try {
    const value: unknown = JSON.parse(stored ?? "{}")
    if (Array.isArray(value)) {
      return {
        ...defaultFolds,
        ...Object.fromEntries(value.filter(isSection).map((s) => [s, true])),
      }
    }
    if (typeof value !== "object" || value === null) return defaultFolds
    return {
      ...defaultFolds,
      ...Object.fromEntries(
        Object.entries(value).filter(
          (entry): entry is [SidebarSection, boolean] =>
            isSection(entry[0]) && typeof entry[1] === "boolean",
        ),
      ),
    }
  } catch {
    return defaultFolds
  }
}

export function readFolds(): Folds {
  try {
    return parseFolds(localStorage.getItem(storageKey))
  } catch {
    return defaultFolds
  }
}

/** Remembers one section's fold, keeping what is stored for the others. */
export function writeFold(section: SidebarSection, folded: boolean) {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ ...readFolds(), [section]: folded }))
  } catch {
    // Not remembered; the sidebar still folds for as long as the page is open.
  }
}
